/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import {
  useMeInfo,
  useMenuCategories,
  useMenuDetail,
  useMenus,
  usePostPreSignedUrl,
  useUpdateMenu,
  useUpdateMenuSort
} from 'hooks';
import { Button, Dropdown, Input, Row, Space } from 'antd';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  CategoryWrapper,
  ContentWrapper,
  MenuSearchInputWrapper,
  SearchOptionButtonGroup,
  StickySearchWrapper,
  StyledDropdownButton
} from './Style';
import MenuCard from './MenuCard';
import MenuUpdateModal from './MenuUpdateModal';
import { IMenuCategories, IMenuDetailData, IMenuItem, TMenuStatus } from './MenuManageSchema';

const MenuManage = () => {
  const { selectedStore } = useMeInfo();
  const storeId = selectedStore?.sid;
  const isFoodMallSupplyType = selectedStore?.supplyType === 7;
  // 상태 버튼 관련 로직
  const [activeStatuses, setActiveStatuses] = useState<{ ACTIVE: boolean; SOLD_OUT: boolean; INACTIVE: boolean }>({
    ACTIVE: true,
    SOLD_OUT: true,
    INACTIVE: false
  });
  const statusParams = useMemo(() => {
    const statuses: number[] = [];
    if (activeStatuses.ACTIVE) statuses.push(1);
    if (activeStatuses.SOLD_OUT) statuses.push(2);
    if (activeStatuses.INACTIVE) statuses.push(0);
    return statuses.length > 0 ? { status: statuses.join(',') } : undefined;
  }, [activeStatuses]);
  const handleButtonClick = (status: TMenuStatus) => {
    setActiveStatuses((prev) => ({
      ...prev,
      [status]: !prev[status]
    }));
  };
  // 메뉴 리스트 관련 로직
  const { data: menuListsData, refetch: refetchMenuLists } = useMenus(storeId, statusParams);
  const { menu: menuLists }: { menu: IMenuItem[] } = menuListsData || {};
  // 카테고리 관련 로직
  const { data: menuCategories }: { data: IMenuCategories[] } = useMenuCategories(storeId);
  const menuCategoriesSelectOptions = useMemo(() => {
    return menuCategories?.map(({ categoryId, categoryName }) => ({
      label: categoryName,
      value: categoryId
    }));
  }, [menuCategories]);
  const { mutateAsync: postPreSignedUrl } = usePostPreSignedUrl();
  // PRE-SIGNED URL 발급 로직
  const getPreSignedUrl = async (values: { menuData: IMenuItem; file: File }) => {
    const { menuData, file } = values;
    const params = {
      serviceName: 'store-menu-thumbnail',
      storeId,
      fileName: file.name,
      filePath: `menu/${menuData.id}`
    };
    return await postPreSignedUrl(params);
  };
  // 모달 관련 로직
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [clickedMenuId, setClickedMenuId] = useState<string | null>(null);
  const [updateMenuData, setUpdateMenuData] = useState<IMenuDetailData | null>(null);
  const { data: menuDetailData }: { data: IMenuDetailData | null } = useMenuDetail(storeId, clickedMenuId || '');

  useEffect(() => {
    if (menuDetailData) {
      setUpdateMenuData(menuDetailData);
    }
  }, [menuDetailData]);

  const handleOpenUpdateModal = (menuData: IMenuItem) => {
    setClickedMenuId(menuData.id);
    setIsModalVisible(true);
  };

  const handleCloseUpdateModal = async () => {
    await refetchMenuLists();
    setClickedMenuId(null);
    setUpdateMenuData(null);
    setIsModalVisible(false);
  };
  // 드래그 앤 드롭 순서변경 로직
  const [activeCategoryId, setActiveCategoryId] = useState<number>(0);
  const [localMenuList, setLocalMenuList] = useState<IMenuItem[]>(menuLists);
  const { mutateAsync: updateMenuSortAsync } = useUpdateMenuSort();

  useEffect(() => {
    window.addEventListener('dragover', (event) => event.preventDefault());
    window.addEventListener('drop', (event) => event.preventDefault());
    return () => {
      window.addEventListener('dragover', (event) => event.preventDefault());
      window.addEventListener('drop', (event) => event.preventDefault());
    };
  }, []);

  useEffect(() => {
    setLocalMenuList(menuLists);
  }, [menuLists]);

  const handleDragStart = (categoryId: number) => setActiveCategoryId(categoryId);
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      let reorderedCategoryMenus: IMenuItem[] = [];

      setLocalMenuList((items) => {
        const categoryMenus = items?.filter((item) => Number(item.categoryId) === activeCategoryId);
        const oldIndex = categoryMenus.findIndex((item) => item.id === active.id);
        const newIndex = categoryMenus.findIndex((item) => item.id === over.id);

        reorderedCategoryMenus = arrayMove(categoryMenus, oldIndex, newIndex).map((item, index) => {
          return { ...item, priority: index + 1 };
        });

        const otherMenus = items.filter((item) => Number(item.categoryId) !== activeCategoryId);
        const updatedItems = [...otherMenus, ...reorderedCategoryMenus].sort((a, b) => {
          if (a.categoryId !== b.categoryId) {
            return (
              items.findIndex((item) => item.categoryId === a.categoryId) -
              items.findIndex((item) => item.categoryId === b.categoryId)
            );
          }
          return 0;
        });

        return updatedItems;
      });

      const params = {
        categoryId: activeCategoryId,
        menus: reorderedCategoryMenus.map((item) => {
          return {
            menuId: item.id,
            priority: item.priority
          };
        })
      };
      await updateMenuSortAsync({ sid: storeId, params });
      await refetchMenuLists();
    }
  };

  const buttonMouseDown = (event: React.MouseEvent<Element>) => event.stopPropagation();
  // 검색 관련 로직
  const [searchValue, setSearchValue] = useState<string>('');
  // 검색어 존재 시 순서 변경 불가
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: searchValue ? Infinity : 0
      }
    })
  );
  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  // 메뉴 상태 일괄 변경 로직
  const { mutateAsync: updateMenuStatusAsync } = useUpdateMenu();

  const handleStatusDropdownClick = (event: React.MouseEvent<Element>) => event.stopPropagation();

  const handleMenuStatusAllChange = async (info: MenuInfo, categoryId: number) => {
    const filteredMenus =
      menuLists?.filter((menu) => Number(menu.categoryId) === categoryId && menu.name.includes(searchValue)) || [];
    console.log('handleMenuStatusAllChange', info.key, filteredMenus);
    await updateMenuStatusAsync({ sid: storeId, data: { menuIds: filteredMenus.map((menu) => menu.id), status: info.key } });
    await refetchMenuLists();
  };

  return (
    <>
      <StickySearchWrapper>
        <SearchOptionButtonGroup>
          <div className="search-option-title">검색조건</div>
          <div className="search-option-buttons">
            <Button
              type={activeStatuses.ACTIVE ? 'primary' : 'default'}
              className={activeStatuses.ACTIVE ? 'btn-success' : ''}
              onClick={() => handleButtonClick('ACTIVE')}
              onMouseDown={buttonMouseDown}
            >
              판매중
            </Button>
            <Button
              type={activeStatuses.SOLD_OUT ? 'primary' : 'default'}
              className={activeStatuses.SOLD_OUT ? 'btn-warning' : ''}
              onClick={() => handleButtonClick('SOLD_OUT')}
              onMouseDown={buttonMouseDown}
            >
              일시품절
            </Button>
            <Button
              type={activeStatuses.INACTIVE ? 'primary' : 'default'}
              className={activeStatuses.INACTIVE ? 'btn-danger' : ''}
              onClick={() => handleButtonClick('INACTIVE')}
              onMouseDown={buttonMouseDown}
            >
              판매종료
            </Button>
          </div>
        </SearchOptionButtonGroup>

        <MenuSearchInputWrapper>
          <Input.Search
            placeholder="메뉴명을 입력해주세요."
            allowClear
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onSearch={(v) => handleSearch(v)}
            enterButton="검색"
          />
        </MenuSearchInputWrapper>
      </StickySearchWrapper>

      <ContentWrapper>
        {menuCategories?.length > 0 ? (
          menuCategories?.map(({ categoryId, categoryName }) => {
            const filteredMenus =
              menuLists?.filter((menu) => Number(menu.categoryId) === categoryId && menu.name.includes(searchValue)) || [];
            return (
              <CategoryWrapper key={categoryId}>
                <DndContext
                  sensors={sensors}
                  modifiers={[restrictToParentElement]}
                  onDragStart={() => handleDragStart(categoryId)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="caregory-name">
                    <div className="category-name-wrapper">
                      <span>{categoryName}</span>
                      <span className="menu-count">({filteredMenus.length}개)</span>
                    </div>
                    <div className="status-all-change-button-wrapper">
                      <span>
                        <Space direction="vertical">
                          <StyledDropdownButton
                            className="status-all-change-button"
                            $type="ALL_MENU"
                            $backgroundColor="#000000"
                            $borderColor="#000000"
                          >
                            <div onMouseDown={handleStatusDropdownClick} onClick={handleStatusDropdownClick}>
                              <Dropdown.Button
                                disabled={filteredMenus.length === 0}
                                type="primary"
                                menu={{
                                  items: [
                                    {
                                      label: '판매중',
                                      key: 'ACTIVE',
                                      onClick: (info) => handleMenuStatusAllChange(info, categoryId)
                                    },
                                    {
                                      label: '판매종료',
                                      key: 'INACTIVE',
                                      onClick: (info) => handleMenuStatusAllChange(info, categoryId)
                                    },
                                    {
                                      label: '일시품절',
                                      key: 'SOLD_OUT',
                                      onClick: (info) => handleMenuStatusAllChange(info, categoryId)
                                    }
                                  ]
                                }}
                              >
                                판매 상태 일괄 변경
                              </Dropdown.Button>
                            </div>
                          </StyledDropdownButton>
                        </Space>
                      </span>
                    </div>
                  </div>
                  <div className="menu-list-wrapper">
                    {filteredMenus?.length > 0 ? (
                      <Row gutter={[16, 16]} style={{ padding: '16px 0px' }}>
                        <SortableContext items={filteredMenus?.map(({ id }) => id)} strategy={rectSortingStrategy}>
                          {filteredMenus?.map((menu) => (
                            <MenuCard
                              key={menu.id}
                              storeId={storeId}
                              menuData={menu}
                              refetchMenuLists={refetchMenuLists}
                              handleOpenModal={() => handleOpenUpdateModal(menu)}
                              getPreSignedUrl={getPreSignedUrl}
                            />
                          ))}
                        </SortableContext>
                      </Row>
                    ) : (
                      <div className="no-menu-registered">등록 된 메뉴가 존재하지 않습니다.</div>
                    )}
                  </div>
                </DndContext>
              </CategoryWrapper>
            );
          })
        ) : (
          <div>등록 된 메뉴 카테고리가 존재하지 않습니다.</div>
        )}
      </ContentWrapper>

      <MenuUpdateModal
        storeId={storeId}
        menuData={updateMenuData}
        isFoodMallSupplyType={isFoodMallSupplyType}
        menuCategoriesSelectOptions={menuCategoriesSelectOptions}
        refetchMenuLists={refetchMenuLists}
        open={isModalVisible}
        onCancel={() => {
          handleCloseUpdateModal();
        }}
      />
    </>
  );
};

export default MenuManage;
